import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify"; // Import ToastContainer
import "react-toastify/dist/ReactToastify.css"; // Import toast styles
import Swal from "sweetalert2"; // Keep Swal for confirmation dialogs
import BASE_URL from "../config";

const ClientPayments = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const client_id = Number(queryParams.get("id"));

  const [client, setClient] = useState(null);
  const [payments, setPayments] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: "last_payment_date", direction: "asc" });

  useEffect(() => {
    const fetchClientPayments = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${BASE_URL}api/clients/with-payments`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        const clientData = response.data.find(client => client.id === client_id);
        if (clientData) {
          setClient(clientData);
          setPayments(clientData.payments || []);
        } else {
          console.warn("Client not found");
        }
      } catch (error) {
        console.error("Error fetching client payments:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchClientPayments();
  }, [client_id]);

  const deletePayment = async (paymentId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!"
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const token = localStorage.getItem("token");
          await axios.delete(`${BASE_URL}api/payment/delete/${paymentId}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            }
          });
          setPayments(payments.filter(payment => payment.id !== paymentId));
          toast.success("The payment has been deleted successfully.");
        } catch (error) {
          console.error("Error deleting payment:", error);
          toast.error("Failed to delete the payment.");
        }
      }
    });
  };

  const toggleStatus = async (paymentId, currentStatus, nextDueDate, clientId) => {
    const newStatus = currentStatus === "Pending" ? "Paid" : "Pending";
    const confirm = await Swal.fire({
      title: `Mark as ${newStatus}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: `Yes, mark as ${newStatus}`,
    });

    if (confirm.isConfirmed) {
      try {
        const token = localStorage.getItem("token");
        const updateResponse = await axios.put(`${BASE_URL}api/payment/update/${paymentId}`, { status: newStatus }, {
          headers: {
            Authorization: `Bearer ${token}`,
          }
        });

        if (updateResponse.status === 200) {
          // Update the status in the DOM
          setPayments(payments.map(p =>
            p.id === paymentId ? { ...p, status: newStatus } : p
          ));

          toast.success(`Payment marked as ${newStatus}.`);

          // Call the API to create a new record only if the status is updated to "Paid"
          if (newStatus === "Paid") {
            // Call the delete API to remove the record by next_due_date and client_id
            try {
              // Format nextDueDate to "YYYY-MM-DD"
              const formattedDate = new Date(nextDueDate).toISOString().split("T")[0];
              const payload = { date: formattedDate, client_id: clientId };
              // console.log("Delete API Payload:", payload); // Log the formatted payload

              const deleteResponse = await axios.delete(`${BASE_URL}api/tasks/delete-by-date-and-client`, {
                data: payload, // Send date and client_id in the request body
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              });

              // if (deleteResponse.status === 200) {
              //   console.error("Deleted:", deleteResponse.data.message);
              // }
            } 
            catch (deleteError) {
              // console.error("Error deleting record:", deleteError);
              // toast.error("Failed to delete the record.");
            }

            // Call the duplicate API to create a new payment record
            const duplicateResponse = await axios.post(`${BASE_URL}api/payment/duplicate/${paymentId}`, {}, {
              headers: {
                Authorization: `Bearer ${token}`,
              }
            });

            if (duplicateResponse.status === 201) {
              const newPayment = duplicateResponse.data.newPayment;
              setPayments(prevPayments => [...prevPayments, newPayment]); // Add the new payment to the list
              // toast.success("A new payment record has been created.");
            }
          }
        }
      } catch (err) {
        toast.error("Failed to update payment status or create a new record.");
      }
    }
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const sortedPayments = [...payments].sort((a, b) => {
    const aVal = a[sortConfig.key] || "";
    const bVal = b[sortConfig.key] || "";
    if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });

  const indexOfLastPayment = currentPage * pageSize;
  const indexOfFirstPayment = indexOfLastPayment - pageSize;
  const currentPayments = sortedPayments.slice(indexOfFirstPayment, indexOfLastPayment);
  const totalPages = Math.ceil(payments.length / pageSize);

  return (
    <div className="clients mt-4 mx-3">
      <ToastContainer /> {/* Add ToastContainer here */}
      <h2 className="mb-3">Payment History</h2>

      {loading ? (
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <>
          <div className="d-flex justify-content-between mb-3">
            <a href={`/add-payment?id=${client_id}`} className="btn btn-primary">Add Payment</a>
            <a href="/clients" className="btn btn-secondary">Back to Clients</a>
          </div>

          {client ? <h4>Payment Mode: {client.payment_mode}</h4> : <p>Loading client data...</p>}

          <div className="mb-3">
            <label>Show: </label>
            <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} className="form-select d-inline-block w-auto ms-2">
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="15">15</option>
              <option value="20">20</option>
            </select> payments
          </div>

          <table className="table table-striped">
            <thead className="table-dark">
              <tr>
                <th style={{ cursor: "pointer" }} onClick={() => handleSort("last_payment_date")}>
                  Payment Date {sortConfig.key === "last_payment_date" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}
                </th>
                <th style={{ cursor: "pointer" }} onClick={() => handleSort("next_due_date")}>
                  Next Due Date {sortConfig.key === "next_due_date" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}
                </th>
                <th style={{ cursor: "pointer" }} onClick={() => handleSort("status")}>
                  Status {sortConfig.key === "status" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentPayments.length > 0 ? (
                currentPayments.map(payment => (
                  <tr key={payment.id} className={payment.status === "Pending" || payment.status === null ? "table-warning" : "table-success"}>
                    <td>{formatDate(payment.last_payment_date)}</td>
                    <td>{formatDate(payment.next_due_date)}</td>
                    <td className={payment.status === "Pending" || payment.status === null ? "text-danger" : "text-success"}>
                      <span
                        style={{ cursor: "pointer", textDecoration: "underline" }}
                        onClick={() => toggleStatus(payment.id, payment.status || "Pending", payment.next_due_date, 
                          client_id)}
                      >
                        {payment.status || "Pending"}
                      </span>
                    </td>
                    <td>
                      <button className="btn btn-sm btn-danger" onClick={() => deletePayment(payment.id)}>Delete</button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan="4">No payments found.</td></tr>
              )}
            </tbody>
          </table>

          <div className="d-flex justify-content-between align-items-center">
            <p className="mb-0">Page {currentPage} of {totalPages}</p>
            <div>
              <button className="btn btn-secondary me-2" disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>Previous</button>
              <button className="btn btn-secondary" disabled={currentPage === totalPages || totalPages === 0} onClick={() => setCurrentPage(currentPage + 1)}>Next</button>
            </div>
          </div>

          <a href="/clients" className="btn btn-secondary mt-3">Back to Clients</a>
        </>
      )}
    </div>
  );
};

export default ClientPayments;
